import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';

import { CircleButton } from '@/lib/shared/components/Buttons';
import { modal } from '@/lib/shared/components/Modal';
import { P1, P2 } from '@/lib/shared/components/Typography';
import { useWindowSize } from '@/lib/shared/hooks/use-window-size';
import { ArrowIcon } from '@/lib/shared/icons';
import { IClassname } from '@/lib/shared/model/classname';

import { clsx } from 'clsx';
import cn from 'clsx';
import { useRouter } from 'next/router';

import styles from './styles.module.scss';

interface IGalleryModal extends IClassname {
  tabHeaders: string[];
  tabContents: React.ReactNode[];
  initialTabIndex?: number;
  onTabChange?: (index: number) => void;
  onBackButtonClick?: () => void;
  leftHeaderContent?: React.ReactNode;
  rightHeaderContent?: React.ReactNode;
  footer?: React.ReactNode;
  mobileTopTabs?: boolean;
  paddedMobileTopTabs?: boolean;
  bodyClassName?: string;
}

export const GalleryTabsModal = ({
  tabHeaders,
  tabContents,
  leftHeaderContent,
  rightHeaderContent,
  initialTabIndex,
  onTabChange,
  onBackButtonClick,
  className,
  footer,
  mobileTopTabs,
  paddedMobileTopTabs,
  bodyClassName,
}: IGalleryModal) => {
  const { width: windowWidth } = useWindowSize();
  const isTabAnimated = useRef(false);
  const [selected, setSelected] = useState(initialTabIndex);
  const tabListState = useTabListState({
    tabHeaders,
    isTabAnimated,
    windowWidth,
    selected,
  });

  const onSelect = useCallback(
    (index: number) => {
      setSelected(index);
      onTabChange?.(index);
    },
    [onTabChange, setSelected]
  );

  return (
    <Tabs
      tabIndex={selected}
      defaultIndex={initialTabIndex}
      onSelect={onSelect}
      className={cn(styles.galleryModal, className)}
    >
      <div
        className={cn(
          styles.galleryModal__header,
          styles['galleryModal__header--tabs']
        )}
      >
        <CircleButton
          as="button"
          theme="mixed"
          onClick={onBackButtonClick || (() => modal.close())}
          name="return button"
          content={<ArrowIcon />}
          className={styles.backBtn}
        />
        {leftHeaderContent}
        {tabHeaders &&
          (!windowWidth || windowWidth >= 996) &&
          RenderTabList({
            tabHeaders,
            isTabAnimated,
            ...tabListState,
          })}
        <div className={styles.galleryModal__headerActions}>
          {rightHeaderContent}
        </div>
      </div>
      {tabHeaders &&
        paddedMobileTopTabs &&
        windowWidth &&
        windowWidth < 996 &&
        mobileTopTabs && (
          <div
            className={cn(
              styles.galleryModal__header,
              styles['galleryModal__header--tabs'],
              styles['galleryModal__header--padded']
            )}
          >
            {RenderTabList({
              tabHeaders,
              isTabAnimated,
              ...tabListState,
            })}
          </div>
        )}
      <div className={clsx(styles.galleryModal__body, bodyClassName)}>
        {tabContents.map(
          (tabContent, i) =>
            tabContent != null && (
              <TabPanel key={i} className={styles.tabPanel}>
                {tabContent}
              </TabPanel>
            )
        )}
      </div>
      {tabHeaders &&
        windowWidth &&
        windowWidth < 996 &&
        !mobileTopTabs &&
        RenderTabList({
          tabHeaders,
          isTabAnimated,
          ...tabListState,
        })}
      {footer}
    </Tabs>
  );
};

type RenderTabListProps = {
  tabHeaders: string[];
  isTabAnimated: MutableRefObject<boolean>;
  windowWidth?: number;
  selected?: number;
  theme?:
    | 'light'
    | 'dark'
    | 'top-border'
    | 'bottom-border'
    | 'bottom-border-light';
  className?: string;
  offset: number;
  width: number;
  tabListRef: MutableRefObject<HTMLDivElement | null>;
};

export const useTabListState = ({
  tabHeaders,
  isTabAnimated,
  windowWidth,
  selected,
}: Pick<
  RenderTabListProps,
  'tabHeaders' | 'isTabAnimated' | 'windowWidth' | 'selected'
>) => {
  const [offset, setOffset] = useState(0);
  const router = useRouter();
  const locale = router.locale;

  const [width, setWidth] = useState(0);
  const tabListRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!tabListRef.current) return;
    const selectedItem: HTMLDivElement | null =
      tabListRef.current.querySelector('.react-tabs__tab--selected');

    if (selectedItem) {
      setOffset(selectedItem.offsetLeft);
      setWidth(selectedItem.clientWidth);
      setTimeout(() => {
        isTabAnimated.current = true;
      }, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, windowWidth, tabHeaders, locale]);

  return { offset, width, tabListRef };
};

export const RenderTabList = ({
  tabHeaders,
  isTabAnimated,
  theme = 'light',
  className,
  offset,
  width,
  tabListRef,
}: RenderTabListProps) => {
  const isBorderTab = theme && theme.includes('border');
  return (
    <TabList
      className={clsx(
        styles.tabs,
        styles.tab__list,
        theme === 'dark' && styles['tab__list--dark'],
        theme === 'top-border' && styles['tab__list--top-border'],
        theme === 'bottom-border' && styles['tab__list--bottom-border'],
        theme === 'bottom-border-light' &&
          styles['tab__list--bottom-border-light'],
        className
      )}
    >
      <div
        className={styles.tab__tracker}
        style={{
          width,
          transform: `translateX(${offset}px)`,
          transition: isTabAnimated.current ? '.25s all' : 'none',
        }}
      />
      <div ref={tabListRef} style={{ display: 'flex' }}>
        {tabHeaders.map((name) => (
          <Tab className={clsx(styles.tab__button)} key={name}>
            {isBorderTab ? <P1>{name}</P1> : <P2>{name}</P2>}
          </Tab>
        ))}
      </div>
    </TabList>
  );
};
